/**
 * Studio Series API Client
 *
 * Wraps backend contract `studio-series-courses-api-contract-v3.md` §5 + §5.13.
 *
 * Endpoints:
 *   GET    /studio/series                                 → list (paginated)
 *   GET    /studio/series/{uuid}                          → detail
 *   POST   /studio/series                                 → create (multipart)
 *   POST   /studio/series/{uuid}                          → update (multipart)
 *   DELETE /studio/series/{uuid}/thumbnail                → clear thumbnail
 *   POST   /studio/series/{uuid}/submit-for-review        → lifecycle
 *   POST   /studio/series/{uuid}/withdraw-submission      → lifecycle
 *   PATCH  /studio/series/{uuid}/unpublish                → lifecycle
 *   PATCH  /studio/series/{uuid}/publish                  → lifecycle (creator self-publish, series only)
 *   DELETE /studio/series/{uuid}                          → hard delete (series only)
 *   POST   /studio/series/{uuid}/videos/{video:uuid}      → attach
 *   DELETE /studio/series/{uuid}/videos/{video:uuid}      → detach
 *   PATCH  /studio/series/{uuid}/videos/reorder           → bulk reorder
 *   PATCH  /studio/videos/{uuid}/move                     → atomic cross-parent move (§5.13)
 *
 * BE registers update routes as POST only — do NOT spoof `_method=PATCH`
 * (returns 405 MethodNotAllowed).
 */

import type {
  AttachVideoResponse,
  CreateStudioSeriesPayload,
  DetachVideoResponse,
  MoveVideoPayload,
  MoveVideoResponse,
  ReorderVideosPayload,
  ReorderVideosResponse,
  StudioSeries,
  StudioSeriesDetail,
  StudioSeriesDetailResponse,
  StudioSeriesListFilters,
  StudioSeriesListResponse,
  StudioSeriesResponse,
  UpdateStudioSeriesPayload,
} from '@/types/studio-series';
import { apiClient } from './base-client';

function buildSeriesFormData(
  payload: CreateStudioSeriesPayload | UpdateStudioSeriesPayload
): FormData {
  const form = new FormData();
  if (payload.title !== undefined) form.append('title', payload.title);
  if (payload.slug !== undefined) form.append('slug', payload.slug);
  if (payload.description !== undefined) form.append('description', payload.description);
  if (payload.language !== undefined) form.append('language', payload.language);
  if (payload.thumbnail !== undefined) form.append('thumbnail', payload.thumbnail);
  if (payload.categories !== undefined) {
    payload.categories.forEach((id, i) => form.append(`categories[${i}]`, String(id)));
  }
  return form;
}

function buildListParams(filters?: StudioSeriesListFilters): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  if (filters?.page !== undefined) params.page = filters.page;
  if (filters?.per_page !== undefined) params.per_page = Math.min(filters.per_page, 100);
  if (filters?.status !== undefined) params.status = filters.status;
  if (filters?.q !== undefined) params.q = filters.q;
  return params;
}

export const studioSeriesClient = {
  list: async (
    filters?: StudioSeriesListFilters,
    serverToken?: string
  ): Promise<StudioSeriesListResponse> => {
    const params = buildListParams(filters);
    return apiClient.get<StudioSeriesListResponse>('/studio/series', {
      ...(Object.keys(params).length > 0 ? { params } : {}),
      ...(serverToken !== undefined ? { serverToken } : {}),
    });
  },

  get: async (uuid: string, serverToken?: string): Promise<StudioSeriesDetail> => {
    const data = await apiClient.get<StudioSeriesDetailResponse>(
      `/studio/series/${uuid}`,
      serverToken !== undefined ? { serverToken } : undefined
    );
    return data.series;
  },

  create: async (payload: CreateStudioSeriesPayload): Promise<StudioSeries> => {
    const form = buildSeriesFormData(payload);
    const data = await apiClient.post<StudioSeriesResponse>('/studio/series', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data.series;
  },

  update: async (uuid: string, payload: UpdateStudioSeriesPayload): Promise<StudioSeries> => {
    const form = buildSeriesFormData(payload);
    const data = await apiClient.post<StudioSeriesResponse>(`/studio/series/${uuid}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data.series;
  },

  clearThumbnail: async (uuid: string): Promise<StudioSeries> => {
    const data = await apiClient.delete<StudioSeriesResponse>(`/studio/series/${uuid}/thumbnail`);
    return data.series;
  },

  submitForReview: async (uuid: string): Promise<StudioSeries> => {
    const data = await apiClient.post<StudioSeriesResponse>(
      `/studio/series/${uuid}/submit-for-review`,
      {}
    );
    return data.series;
  },

  withdrawSubmission: async (uuid: string): Promise<StudioSeries> => {
    const data = await apiClient.post<StudioSeriesResponse>(
      `/studio/series/${uuid}/withdraw-submission`,
      {}
    );
    return data.series;
  },

  unpublish: async (uuid: string): Promise<StudioSeries> => {
    const data = await apiClient.patch<StudioSeriesResponse>(
      `/studio/series/${uuid}/unpublish`,
      {}
    );
    return data.series;
  },

  /**
   * Creator-callable publish — series skip admin review (they're playlists).
   * 422 `errors: { title?, description?, lessons?, thumbnail? }` lists every
   * failed gate at once; 409 `ALREADY_PUBLISHED` when already live.
   */
  publish: async (uuid: string): Promise<StudioSeries> => {
    const data = await apiClient.patch<StudioSeriesResponse>(`/studio/series/${uuid}/publish`, {});
    return data.series;
  },

  /**
   * Hard delete. Episodes are detached (back to standalone), not deleted;
   * modules and the cover go with the row. Not recoverable — confirm first.
   */
  remove: async (uuid: string): Promise<void> => {
    await apiClient.delete<{ message: string }>(`/studio/series/${uuid}`);
  },

  attachVideo: async (seriesUuid: string, videoUuid: string): Promise<AttachVideoResponse> => {
    return apiClient.post<AttachVideoResponse>(
      `/studio/series/${seriesUuid}/videos/${videoUuid}`,
      {}
    );
  },

  detachVideo: async (seriesUuid: string, videoUuid: string): Promise<DetachVideoResponse> => {
    return apiClient.delete<DetachVideoResponse>(
      `/studio/series/${seriesUuid}/videos/${videoUuid}`
    );
  },

  reorderVideos: async (
    seriesUuid: string,
    payload: ReorderVideosPayload
  ): Promise<ReorderVideosResponse> => {
    return apiClient.patch<ReorderVideosResponse>(
      `/studio/series/${seriesUuid}/videos/reorder`,
      payload
    );
  },

  moveVideo: async (videoUuid: string, payload: MoveVideoPayload): Promise<MoveVideoResponse> => {
    return apiClient.patch<MoveVideoResponse>(`/studio/videos/${videoUuid}/move`, payload);
  },
};
